import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Shield, AlertTriangle, AlertCircle, CheckCircle, Info, XCircle, BadgeCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const badges = [
  {
    status: "safe" as const,
    title: "Established Website",
    color: "Green",
    meaning: "The domain is well-established, has a valid SSL certificate, and no threats were found in public databases at the time of checking.",
    indicators: [
      "No matches in public phishing or malware databases",
      "Domain has been registered for 6+ months",
      "Valid SSL certificate with HTTPS enabled",
      "No abuse reports in threat intelligence feeds",
    ],
    whatItMeans: "This result comes from automatic checks only — no human has reviewed this website. It means the domain has been around for a while and has no known issues. It does NOT guarantee the site is trustworthy or that the business behind it is legitimate.",
    bgClass: "bg-safe-bg",
    borderClass: "border-safe/30",
    textClass: "text-safe",
  },
  {
    status: "caution" as const,
    title: "New / Unknown Website",
    color: "Yellow",
    meaning: "We don't have enough data to confirm this website's safety. It may be a recently registered domain with limited history.",
    indicators: [
      "Domain registered less than 6 months ago",
      "Limited or no history in safety databases",
      "No negative signals, but also no positive track record",
      "SSL may be present but domain reputation is unestablished",
    ],
    whatItMeans: "This doesn't mean the site is dangerous. Many real businesses start with a new domain. But be careful before sharing personal info or making payments — the site simply hasn't built enough history for us to confirm it's safe.",
    bgClass: "bg-caution-bg",
    borderClass: "border-caution/30",
    textClass: "text-caution",
  },
  {
    status: "risk" as const,
    title: "Risky / Potential Scam",
    color: "Red",
    meaning: "Our scan detected one or more warning signals. The domain may be associated with phishing, malware, or other malicious activity.",
    indicators: [
      "Flagged in public threat intelligence feeds for phishing or malware",
      "Listed in abuse databases or spam registers",
      "Domain name resembles known brand impersonation patterns",
      "Multiple negative signals found across data sources",
    ],
    whatItMeans: "Avoid entering personal information, payment details, or downloading files from this site. If you've already interacted with it, read our guide on what to do after visiting a scam website.",
    bgClass: "bg-risk-bg",
    borderClass: "border-risk/30",
    textClass: "text-risk",
  },
];

export default function BadgeMeaning() {
  return (
    <Layout>
      <Helmet>
        <title>What Do Website Safety Results Mean? Badge Color Guide | Transparency Badge</title>
        <meta name="description" content="Understand what green, yellow, red, and verified results mean when you check a website. Each color represents a different trust level based on real data." />
        <link rel="canonical" href="https://transparencybadge.com/badge-meaning" />
      </Helmet>

      {/* Hero */}
      <section className="border-b bg-gradient-to-b from-accent/50 to-background py-16 md:py-20">
        <div className="container">
          <SectionHeader
            as="h1"
            badge="Result Guide"
            title="What Your Website Check Result Means"
            description="When you check a website, you'll see one of four results. Here's what each one actually tells you — and what it doesn't."
          />
        </div>
      </section>

      {/* Badge Explanations */}
      <section className="py-16 md:py-20">
        <div className="container max-w-4xl">
          <div className="space-y-8">

            {/* Verified Badge */}
            <div className="rounded-2xl border-2 border-primary/40 p-8 animate-fade-in bg-accent/30">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex flex-col items-center md:items-start shrink-0">
                  <div className="w-20 h-20 rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center">
                    <BadgeCheck className="h-10 w-10 text-primary" />
                  </div>
                  <span className="mt-3 text-lg font-bold text-primary">Teal Badge</span>
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-2xl font-bold text-foreground">Verified by Transparency Badge</h3>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">Highest Trust</span>
                  </div>
                  <p className="text-muted-foreground mb-6">
                    The site owner applied, passed our one-time eligibility check, and pays for ongoing automated monitoring to keep this badge active. This is the highest level of trust available on our platform.
                  </p>

                  <div className="space-y-6">
                    <div>
                      <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-3">
                        What Was Checked
                      </h4>
                      <ul className="space-y-2">
                        {[
                          "Passed automatic threat scans — same checks as the free tool",
                          "Passed a one-time eligibility check against published criteria",
                          "Website purpose and business legitimacy confirmed",
                          "Monitored every month while subscription is active",
                          "Badge is immediately revoked if the site later fails safety checks",
                        ].map((indicator, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <CheckCircle className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                            <span>{indicator}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-background/50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                        <Info className="h-4 w-4" />
                        Why This Matters
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        The green "Established" result is automatic — any old domain with valid SSL gets it. But "Verified" means the owner actively chose to be checked against our published criteria, and pays for ongoing monitoring to keep that status active. That's a deliberate commitment to trust.
                      </p>
                    </div>

                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">Run a website?</span>{" "}
                        <Link to="/get-verified" className="text-primary underline underline-offset-2">
                          Apply for the Verified Badge — first month free →
                        </Link>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Automatic badges */}
            {badges.map((badge, index) => (
              <div
                key={index}
                className={cn(
                  "rounded-2xl border p-8 animate-fade-in",
                  badge.bgClass,
                  badge.borderClass
                )}
                style={{ animationDelay: `${(index + 1) * 0.1}s` }}
              >
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex flex-col items-center md:items-start shrink-0">
                    <StatusBadge status={badge.status} size="lg" showLabel={false} />
                    <span className={cn("mt-3 text-lg font-bold", badge.textClass)}>
                      {badge.color} Badge
                    </span>
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-2xl font-bold text-foreground">{badge.title}</h3>
                      {badge.status === "safe" && (
                        <span className="text-xs bg-safe/10 text-safe px-2 py-0.5 rounded-full font-medium">Automatic Only</span>
                      )}
                    </div>
                    <p className="text-muted-foreground mb-6">{badge.meaning}</p>

                    <div className="space-y-6">
                      <div>
                        <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-3">
                          What Triggers This Result
                        </h4>
                        <ul className="space-y-2">
                          {badge.indicators.map((indicator, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <CheckCircle className={cn("h-4 w-4 mt-0.5 shrink-0", badge.textClass)} />
                              <span>{indicator}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-background/50 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                          <Info className="h-4 w-4" />
                          What This Means for You
                        </h4>
                        <p className="text-sm text-muted-foreground">{badge.whatItMeans}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deep Explanation */}
      <section className="py-16 md:py-20 border-t">
        <div className="container max-w-3xl">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            How to Use These Results to Make Better Decisions
          </h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              Website safety results are tools for decision-making, not final judgments. Understanding what each result means — and what it doesn't — helps you use this information in a practical way rather than treating it as a simple pass/fail grade.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">When You See a Green Result</h3>
            <p>
              A green "Established" result means the domain has been around for a meaningful period of time, has a valid SSL certificate, and hasn't been flagged in public threat databases. This is a genuinely good sign. Long-running scam operations are the exception, not the rule — most fraudulent websites are short-lived by necessity.
            </p>
            <p>
              However, a green result is automatic. No human has looked at this website. It means the data looks clean, not that the business is verified. You should still use normal caution — check the returns policy before buying, look for contact information, and trust your instincts if something feels off about the product or price. For high-value purchases or unfamiliar stores, consider checking reviews on independent platforms as well.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">When You See a Yellow Result</h3>
            <p>
              Yellow doesn't mean danger. It means we don't have enough data to confirm safety one way or the other. The most common cause is a recently registered domain — a site that simply hasn't been around long enough to build a track record.
            </p>
            <p>
              Many completely legitimate businesses launch with new domains every day. A new freelancer, a startup's first landing page, a small online store that opened last month — all of these would show yellow on our tool. What yellow tells you is: exercise more care than you would with an established site. Look for other trust signals before sharing payment details: social media presence, reviews on third-party platforms, verifiable contact information, and a clear returns or refund policy.
            </p>
            <p>
              If you're the owner of a new website showing yellow and you want to change that result, the Verified Badge Program exists for exactly this reason. A one-time eligibility check against our published criteria is the way to go from "Unknown" to "Verified" before your domain has had years to build organic history.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">When You See a Red Result</h3>
            <p>
              A red result means our scan found a specific, concrete warning: the domain appeared in a phishing database, was associated with malware distribution, or has been reported in security feeds as malicious. This is a serious signal and should not be ignored.
            </p>
            <p>
              We recommend not entering any personal information, passwords, or payment details on a red-flagged site. If you've already interacted with the site — clicked a link, entered an email address, or especially if you've entered payment details — read our guide on what to do after visiting a scam website. Acting quickly can minimize the damage.
            </p>
            <p>
              In rare cases, a legitimate website may get flagged if it was recently compromised by hackers, or if a previous owner of the domain used it for malicious purposes. If you believe a site was incorrectly flagged, contact that site's owner and ask them to investigate and get their domain removed from the relevant threat feed.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">When You See a Verified Result</h3>
            <p>
              A teal "Verified" result reflects our highest trust level. The website owner applied to our Verified Badge Program, passed a one-time eligibility check against our published criteria, and is approved for ongoing monitoring. The owner continues paying for that monitoring on a monthly basis — meaning they have ongoing accountability for maintaining the standards that got them approved.
            </p>
            <p>
              This is meaningfully different from the automatic checks. Automatic checks alone can't confirm business legitimacy, read terms of service, or evaluate whether product descriptions match what's actually being sold. The eligibility check covers that ground once, at onboarding, and the recurring subscription is what keeps the domain under continuous, automated monitoring after that — not a repeated manual review.
            </p>
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-3">Results Are a Snapshot, Not a Permanent Record</h3>
            <p>
              Website status changes over time. A safe, established website can be hacked and start distributing malware without the owner even knowing. A previously risky domain can be cleaned up and reclaimed by a legitimate owner. Our results reflect what the data says at the time of your check — they are not a permanent certification of good or bad standing.
            </p>
            <p>
              For this reason, we recommend checking a website at the time you're about to interact with it — not relying on a check you did three months ago. The check takes seconds, and the data may have changed.
            </p>
          </div>
        </div>
      </section>

      {/* Disclaimers */}
      <section className="py-16 md:py-20 bg-muted/30 border-y">
        <div className="container max-w-3xl">
          <SectionHeader
            title="Things to Keep in Mind"
            description="Our results are based on real data — but they're not guarantees."
            align="center"
          />

          <div className="grid gap-4 md:grid-cols-2 mt-10">
            <div className="bg-card rounded-xl border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <CheckCircle className="h-4 w-4" />
                </div>
                <h4 className="font-semibold text-foreground">Helpful, Not Perfect</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Our results give you real data to make better decisions. But no tool — including ours — can be 100% accurate all the time.
              </p>
            </div>

            <div className="bg-card rounded-xl border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-risk/10 text-risk">
                  <XCircle className="h-4 w-4" />
                </div>
                <h4 className="font-semibold text-foreground">Not a Guarantee</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                A green result doesn't guarantee safety. A red result doesn't prove criminal intent. These are data-driven signals — not legal judgments.
              </p>
            </div>

            <div className="bg-card rounded-xl border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-caution/10 text-caution">
                  <AlertTriangle className="h-4 w-4" />
                </div>
                <h4 className="font-semibold text-foreground">Status Changes Over Time</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Websites change. A safe site can get compromised, and a new site can build a good reputation. Results reflect data at the time of your check.
              </p>
            </div>

            <div className="bg-card rounded-xl border p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Shield className="h-4 w-4" />
                </div>
                <h4 className="font-semibold text-foreground">Trust Your Instincts Too</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Use our data alongside your own judgment. If something feels off about a website — even if it shows green — trust your gut and be cautious.
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
